"use client";

import { DetailHero } from "@/components/detail-hero";
import { DiscAlbum } from "lucide-react";
import { AsyncBoundary, EmptyState } from "@gerama/ui";
import { formatNumber } from "@gerama/utils";
import { useAlbum, useAlbumSongs } from "./use-album";
import { AlbumDetailSkeleton } from "./album-detail-skeleton";
import { SongRow } from "@/features/songs/song-row";
import { PlayButton } from "@/features/player/play-button";
import { songToTrack } from "@/features/player/to-track";
import { publicPath } from "@/lib/public-path";

export function AlbumDetail({ id }: { id: number | string }) {
  const albumQuery = useAlbum(id);
  const songsQuery = useAlbumSongs(id);

  return (
    <AsyncBoundary
      variant="immersive"
      query={albumQuery}
      skeleton={<AlbumDetailSkeleton />}
      isEmpty={() => false}
      empty={
        <EmptyState
          variant="immersive"
          icon={<DiscAlbum className="size-8" />}
          title="آلبوم یافت نشد"
        />
      }
      render={(album) => {
        const songs = songsQuery.data?.list ?? [];
        const queue = songs.map((s) => songToTrack(s));
        return (
          <article className="space-y-4">
            <DetailHero
              kind="آلبوم"
              title={album.name}
              cover={album.cover}
              seed={`album-${album.id}`}
              fallbackIcon={<DiscAlbum className="size-14" />}
              meta={`${formatNumber(album.songCount, "fa")} آهنگ`}
              actions={
                queue[0] && (
                  <PlayButton
                    track={queue[0]}
                    queue={queue}
                    contextLabel={album.name}
                    contextHref={publicPath("albums", album)}
                    appearance="hero"
                  />
                )
              }
            />

            <AsyncBoundary
              variant="immersive"
              query={songsQuery}
              skeleton={<AlbumDetailSkeleton />}
              empty={<EmptyState variant="immersive" title="آهنگی در این آلبوم نیست" />}
              render={(list) => (
                <div className="space-y-1">
                  {list.list.map((song, i) => (
                    <SongRow
                      key={song.id}
                      song={song}
                      index={i}
                      queue={queue}
                      contextLabel={album.name}
                      contextHref={publicPath("albums", album)}
                    />
                  ))}
                </div>
              )}
            />
          </article>
        );
      }}
    />
  );
}
